// @ts-nocheck

import { defineStore } from "pinia";
import { computed, ref, shallowRef, markRaw } from "vue";
import {
  Viewer,
  DefaultViewerParams,
  ViewerEvent,
  SpeckleLoader,
  UrlHelper,
  CameraController,
  SelectionExtension,
  ViewModes,
  LoaderEvent,
  FilterMaterialType,
  UpdateFlags,
  SectionTool,
  FilteringExtension,
  MeasurementsExtension,
  MeasurementEvent,
} from "@speckle/viewer";

import type { Measurement, NodeRenderView } from "@speckle/viewer";
import { useThrottleFn } from "@vueuse/core";
import * as Sentry from "@sentry/vue";

import { useSpeckleFilters } from "@/stores/speckle/speckle.filters";
import { resetPickGuard } from "@/stores/speckle/pick-guard";
import { uninstallPickAssist } from "@/features/Dashboard/components/speckleFilters/measurementPickAssist";
import { VertexDragExtension } from "@/features/Dashboard/components/speckleFilters/VertexDragExtension";
import { hasWebGl2 } from "@/services/Utils/webgl";
import { getSpeckleToken } from "@/api/services/Speckle";

const BASE_URL = import.meta.env.VITE_SPECKLE_URL;

/** ---------- state shared across loads (progress UI) ---------- */
const loading = ref(false);
const loadProgress = ref(0);
const loadingProgressMap: Record<string, number> = {};
let inflightModels = 0;

let rafId = 0;
let targetPct = 0; // 0..1 aggregated from loaders
let visualPct = 0; // eased value we render
let doneTimer: number | null = null;
let lastProgressAt = Date.now();
const SNAP_EPS = 0.005;
const HIDE_DELAY_MS = 300;
const STALL_TIMEOUT_MS = 4000;

const urlsByModel = new Map<string, string[]>();

const pump = () => {
  if (rafId) return;
  rafId = requestAnimationFrame(() => {
    rafId = 0;
    visualPct = visualPct + (targetPct - visualPct) * 0.25;
    if (targetPct >= 1 - SNAP_EPS && visualPct >= 1 - SNAP_EPS) visualPct = 1;
    loadProgress.value = Number(Math.min(visualPct, 1).toFixed(3));
  });
};

const consolidateProgressInternal = ({ progress, id }: { progress: number; id: string }) => {
  loadingProgressMap[id] = Math.max(0, Math.min(1, progress));
  const vals = Object.values(loadingProgressMap);
  targetPct = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 1;
  lastProgressAt = Date.now();
  pump();
};
const consolidateProgressThrottled = useThrottleFn(consolidateProgressInternal, 120);

const startBatchIfNeeded = () => {
  if (inflightModels === 0) {
    loadProgress.value = 0;
    targetPct = 0;
    visualPct = 0;
    lastProgressAt = Date.now();
    if (doneTimer) {
      clearTimeout(doneTimer);
      doneTimer = null;
    }
  }
  inflightModels++;
  loading.value = true;
};

const finalizeProgress = () => {
  targetPct = 1;
  visualPct = 1;
  loadProgress.value = 1;
  pump();
  if (doneTimer) clearTimeout(doneTimer);
  doneTimer = window.setTimeout(() => {
    loading.value = false;
    for (const k of Object.keys(loadingProgressMap)) delete loadingProgressMap[k];
  }, HIDE_DELAY_MS) as unknown as number;
};

const finishBatchIfPossible = () => {
  inflightModels = Math.max(0, inflightModels - 1);
  const nothingLeft = Object.keys(loadingProgressMap).length === 0;
  if (inflightModels === 0 && nothingLeft) finalizeProgress();
};

// Watchdog (stalled loaders)
setInterval(() => {
  const idle = Date.now() - lastProgressAt > STALL_TIMEOUT_MS;
  if (idle && inflightModels === 0 && Object.keys(loadingProgressMap).length === 0 && loading.value) {
    finalizeProgress();
  }
}, 1000);

/** ---------- helpers shared by service/store ---------- */
const getAllNodeIds = (v: Viewer): Set<string> => {
  const wt = v.getWorldTree();
  const nodes = wt.findAll(() => true);
  return new Set(nodes.map((n: any) => String(n.id)));
};

function sanitizeScene(v: Viewer) {
  const wt = v.getWorldTree();
  if (!wt) return { pruned: 0, patched: 0 };

  // prune bad nodes
  const bad = wt.findAll((n: any) => {
    const g: any = n.renderView?.geometry ?? n.geometry;
    if (!g?.attributes) return false;
    const pos = g.attributes.position?.array;
    if (!pos || pos.length % 3 !== 0) return true;
    for (let i = 0; i < pos.length; i++) if (!Number.isFinite(pos[i])) return true;

    const attrs = g.attributes;
    const instStart = (attrs.instanceStart?.data?.array ?? attrs.instanceStart?.array) as Float32Array | undefined;
    const instEnd = (attrs.instanceEnd?.data?.array ?? attrs.instanceEnd?.array) as Float32Array | undefined;
    if (instStart) for (let i = 0; i < instStart.length; i++) if (!Number.isFinite(instStart[i])) return true;
    if (instEnd) for (let i = 0; i < instEnd.length; i++) if (!Number.isFinite(instEnd[i])) return true;
    return false;
  });
  for (const n of bad) wt.removeNode(n, true);

  // patch line/instanced-line geometries
  const lineNodes = wt.findAll((n: any) => {
    const g = n.renderView?.geometry ?? n.geometry;
    return !!g && (g.type === "LineSegmentsGeometry" || "instanceStart" in (g.attributes ?? {}));
  });
  for (const n of lineNodes) {
    const g: any = n.renderView?.geometry ?? n.geometry;
    const instCount = g.attributes?.instanceStart?.count ?? g._maxInstanceCount ?? 0;
    if (!Number.isFinite(g.instanceCount)) g.instanceCount = instCount;
    if (!Number.isFinite(g.drawRange?.count)) g.drawRange.count = g.index?.count ?? Math.max(instCount * 2, 0);
    if (g.boundingSphere && !Number.isFinite(g.boundingSphere.radius)) {
      try {
        g.computeBoundingSphere?.();
      } catch {
        // intentional
      }
      if (!Number.isFinite(g.boundingSphere?.radius)) {
        g.boundingSphere.radius = 0;
        g.boundingSphere.center?.set?.(0, 0, 0);
      }
    }
  }
  v.requestRender?.();
  return { pruned: bad.length, patched: lineNodes.length };
}

// robustly collect NodeRenderView[] for a list of world-tree node ids
function collectRVsForNodeIds(v: Viewer, nodeIds: string[]): NodeRenderView[] {
  const wt = v.getWorldTree();
  const rvs: NodeRenderView[] = [];

  for (const id of nodeIds) {
    const matches = wt.findId(id) || [];
    const node = Array.isArray(matches) ? matches[0] : matches;
    if (!node) continue;

    const rv = (node as any)?.renderView;
    if (rv) {
      rvs.push(rv);
      continue;
    }

    const key1 = String(node.model?.id ?? "");
    const key2 = String(node.id ?? "");
    let rt = key1 ? wt.getRenderTree(key1) : null;
    if (!rt && key2) rt = wt.getRenderTree(key2);

    if (!rt) {
      // fallback: scan all render trees
      const inst = (wt as any).renderTreeInstances || {};
      for (const k of Object.keys(inst)) {
        const candidate = wt.getRenderTree(k);
        const maybe = candidate?.getRenderViewsForNode?.(node) || [];
        if (maybe.length) {
          rvs.push(...maybe);
          break;
        }
      }
    } else {
      const views = rt.getRenderViewsForNode?.(node) || [];
      if (views.length) rvs.push(...views);
    }
  }

  return rvs;
}

// Speckle keeps its own render loop; dispose() does not stop it. Releasing the GPU context inline
// left frames compiling shaders against a dead one (190 errors over 15 cycles) — let the loop wind down first.
const CONTEXT_RELEASE_DELAY_MS = 2000;

function releaseContextWhenIdle(renderer: { forceContextLoss: () => void } | null) {
  if (!renderer) return;
  setTimeout(() => {
    try {
      renderer.forceContextLoss();
    } catch {
      // already gone with the page
    }
  }, CONTEXT_RELEASE_DELAY_MS);
}

export const useSpeckleStore = defineStore("speckle", () => {
  const viewer = shallowRef<Viewer | null>(null);
  const cameraRef = shallowRef<CameraController | null>(null);
  const selectRef = shallowRef<SelectionExtension | null>(null);
  const sectionRef = shallowRef<SectionTool | null>(null);
  const modesRef = shallowRef<InstanceType<typeof ViewModes> | null>(null);
  const filteringRef = shallowRef<FilteringExtension | null>(null);
  const measureRef = shallowRef<MeasurementsExtension | null>(null);
  const vertexDragRef = shallowRef<VertexDragExtension | null>(null);
  const measurementsList = shallowRef<Measurement[]>([]);
  const enableViewer = ref(false);

  const filters = useSpeckleFilters();

  // per-model book-keeping
  const loaded = ref<Set<string>>(new Set());
  const visible = ref<Set<string>>(new Set());
  // Derived, not declared: a plain ref here could go stale and leave Fit/Reset view live over an empty scene.
  const hasModels = computed(() => loaded.value.size > 0);
  const isReady = ref(false);
  // Set when the browser cannot give us a WebGL2 context. Not an error: the callers explain it.
  const webGlUnavailable = ref(false);
  const authToken = ref<null | string>(null);
  const inFlightUrls = new Set<string>();

  // for soft toggle
  const nodeIdsByModel = new Map<string, string[]>();
  const rvsByModel = new Map<string, NodeRenderView[]>();
  const loadingByModel = new Map<string, Promise<void>>();
  // Models whose load was cancelled while still walking their urls; the load loop checks before each url.
  const cancelledModels = new Set<string>();
  // Bumped by disposeViewer. A load that outlives its viewer compares against it after every await.
  let viewerEpoch = 0;
  let viewerContainer: HTMLElement | null = null;
  // The SDK's Input keeps its container listeners after Viewer.dispose() (a no-op), so a discarded
  // extension can still emit; keep the handler to unsubscribe it explicitly in disposeViewer.
  let measurementsChangedHandler: ((list: Measurement[]) => void) | null = null;

  /** The viewer to act on, or null when nothing is left to act on: a caller reports its own rejection (Sentry + toast), so throwing here put a red error banner on a page the user had already left. `viewerEpoch` (bumped only by `disposeViewer`) tells a legitimate "no viewer yet" apart from real misuse — null at epoch 0 still throws. */
  function viewerForAction(): Viewer | null {
    if (viewer.value) return viewer.value;
    if (viewerEpoch > 0) return null;
    throw new Error("Speckle viewer not initialized. Call initViewer(container) first.");
  }

  async function initViewer(container: HTMLElement) {
    if (viewer.value && viewerContainer === container) {
      viewer.value.resize();
      isReady.value = true;
      return;
    }
    // Asked before building anything: `new Viewer` throws deep inside three.js when there is no
    // context to be had, and a device that cannot do WebGL is not a failure worth reporting.
    if (!hasWebGl2()) {
      webGlUnavailable.value = true;
      return;
    }
    webGlUnavailable.value = false;

    // A viewer bound to another container is a leftover of a previous mount: rebuild rather than adopt it.
    if (viewer.value) disposeViewer();
    viewerContainer = container;

    const v = new Viewer(container, {
      ...DefaultViewerParams,
      showStats: false,
      verbose: false,
    });
    await v.init();
    await (v as any).createDefaultExtensions?.();

    cameraRef.value = markRaw(v.createExtension(CameraController));
    // Orbit tuning: orbits the fitted target rather than the cursor point so dragging can't swing the
    // model out of view; capped polar angle stops a full over/under tumble. Setter merges into the option set.
    if (cameraRef.value) {
      cameraRef.value.options = {
        ...cameraRef.value.options,
        orbitAroundCursor: false,
        zoomToCursor: true,
        minimumPolarAngle: 0.05,
        maximumPolarAngle: Math.PI * 0.95,
        damperDecay: 45,
      };
    }
    selectRef.value = markRaw(v.createExtension(SelectionExtension));
    // Element selection is OFF by default: clicks only select/highlight while Inspect is active. Other
    // tools consume clicks via the ObjectClicked event regardless of this flag.
    if (selectRef.value) selectRef.value.enabled = false;
    sectionRef.value = markRaw(v.createExtension(SectionTool));
    modesRef.value = markRaw(v.createExtension(ViewModes));
    measureRef.value = markRaw(v.createExtension(MeasurementsExtension));
    // One completed-measurement list for every panel (Measure's rows, Inspect's measured-vs-modeled);
    // the extension is recreated per viewer, so subscribe here and start empty.
    measurementsList.value = [];
    measurementsChangedHandler = (list) => {
      measurementsList.value = [...list];
    };
    measureRef.value.on(MeasurementEvent.MeasurementsChanged, measurementsChangedHandler);
    // Depends on CameraController + MeasurementsExtension via its own `inject`, so it must register after both.
    vertexDragRef.value = markRaw(v.createExtension(VertexDragExtension));
    const filtering = markRaw(v.createExtension(FilteringExtension));
    filteringRef.value = filtering;
    // Both resetFilters() and setFilters() begin with resetMaterials(), which also drops the renderer-level
    // hide hideModel applies — re-hide after every state change so a hidden model doesn't reappear.
    filtering.on(ViewerEvent.FilteringStateSet, reapplyHiddenModels);

    // Progress: mark resource done on LoadComplete ids
    v.on(ViewerEvent.LoadComplete, (id: string) => {
      if (id in loadingProgressMap) delete loadingProgressMap[id];
      if (inflightModels === 0 && Object.keys(loadingProgressMap).length === 0) finalizeProgress();
    });

    viewer.value = v;
    isReady.value = true;
  }

  function disposeViewer() {
    // Read before disposing, which unsets it. Speckle's dispose frees three's own resources but never calls
    // forceContextLoss, so contexts leak until GC and one tab that reopens the viewer often hits Chrome's ~16-context cap.
    const renderer = (() => {
      try {
        return viewer.value?.getRenderer().renderer ?? null;
      } catch {
        return null;
      }
    })();

    try {
      // The SDK's own render loop never stops on dispose (verified: update() unconditionally
      // reschedules itself, no cancelAnimationFrame anywhere) — overriding it is what actually ends it.
      if (viewer.value) {
        viewer.value.update = () => {};
        viewer.value.render = () => {};
      }
      viewer.value?.dispose();
      renderer?.dispose();
      releaseContextWhenIdle(renderer);
    } catch {
      // Disposing an already-torn-down renderer or lost GL context throws; safe to ignore since
      // the refs below are cleared regardless of whether this block succeeded.
    }
    viewer.value = null;
    viewerContainer = null;
    viewerEpoch += 1;
    // The guard's listeners sit on this container; the next viewer must start from a clean slate.
    resetPickGuard();
    // Same reasoning as the guard above: its listener is also keyed to this container.
    uninstallPickAssist();
    cameraRef.value = null;
    selectRef.value = null;
    // Its listeners sit on the container too, which a rebuilt viewer can reuse.
    vertexDragRef.value?.dispose();
    vertexDragRef.value = null;
    // The extension itself outlives dispose() (see measurementsChangedHandler above) — remove the
    // listener explicitly so a discarded extension can't keep overwriting measurementsList.
    if (measureRef.value && measurementsChangedHandler) {
      measureRef.value.removeListener(MeasurementEvent.MeasurementsChanged, measurementsChangedHandler);
    }
    measureRef.value = null;
    measurementsChangedHandler = null;
    measurementsList.value = [];

    loaded.value.clear();
    visible.value.clear();
    nodeIdsByModel.clear();
    rvsByModel.clear();
    loadingByModel.clear();
    urlsByModel.clear();
    cancelledModels.clear();
    inFlightUrls.clear();

    // progress reset
    for (const k of Object.keys(loadingProgressMap)) delete loadingProgressMap[k];
    inflightModels = 0;
    targetPct = 0;
    visualPct = 0;
    loading.value = false;
    loadProgress.value = 0;
    isReady.value = false;
  }

  async function loadModel(projectId: string, modelId: string) {
    console.log("[speckle] loadModel enter", { projectId, modelId, hasViewer: !!viewer.value });
    const v = viewerForAction();
    if (!v) return;
    const epoch = viewerEpoch;
    const stale = () => epoch !== viewerEpoch || cancelledModels.has(modelId);

    // If already loaded → just show (fast path)
    if (loaded.value.has(modelId) && !visible.value.has(modelId)) {
      console.log("[speckle] loadModel fast-path showModel", modelId);
      await showModel(modelId);
      return;
    }

    // Already on screen: nothing to do. Both the Models filter and Modal3D.applyModels react to the same
    // selection change, and the second caller used to re-download geometry already in the scene.
    if (loaded.value.has(modelId)) return;

    // The in-flight map must be set before the token/URL awaits, or a second caller reacting to the
    // same selection change starts a second full load and the model ends up in the tree twice. Join it instead.
    const inFlight = loadingByModel.get(modelId);
    if (inFlight) {
      await inFlight;
      return;
    }

    const job = (async () => {
      // The token must be in hand before the *first* Speckle call: UrlHelper.getResourceUrls queries the
      // Speckle API unauthenticated otherwise, rendering nothing silently. Refreshing first also keeps the loading counter balanced.
      console.log("[speckle] refreshing token");
      const hasToken = await refreshToken();
      console.log("[speckle] token refreshed, hasToken:", hasToken);
      if (!hasToken) {
        // api/Speckle/access-token 404s when the signed-in user has no linked Speckle account. Fail loudly
        // so the caller can explain it, rather than loading everything unauthenticated into a blank canvas.
        throw new Error("No Speckle access token — the signed-in user has no linked Speckle account.");
      }

      startBatchIfNeeded();

      // snapshot BEFORE
      const before = getAllNodeIds(v);

      const resourceUrl = `${BASE_URL}/projects/${projectId}/models/${modelId}`;
      console.log("[speckle] fetching resource urls", resourceUrl);
      const urls = await UrlHelper.getResourceUrls(resourceUrl, authToken.value);
      console.log("[speckle] got urls", urls);
      if (stale()) return;
      urlsByModel.set(modelId, urls);

      // Load every url in parallel — each is an independent resource with its own render tree entry,
      // and the SDK's own diff feature already runs concurrent loadObject calls the same way.
      await Promise.all(
        urls.map(async (url) => {
          if (stale()) return;
          const loader = new SpeckleLoader(v.getWorldTree(), url, authToken.value);

          // keep your "bad transform" filter
          const filterFn = (raw: unknown) => {
            const transform = (raw as { transform?: unknown } | undefined)?.transform;
            const matrix =
              transform && typeof transform === "object" && "matrix" in transform
                ? (transform as { matrix?: unknown }).matrix
                : transform;
            return !(
              Array.isArray(matrix) &&
              // Number.isFinite safely returns false for non-numbers, so this cast never risks a runtime error.
              matrix.some((component: unknown) => !Number.isFinite(component as number))
            );
          };
          if (typeof loader.setFilter === "function") loader.setFilter(filterFn);
          else loader.filter = filterFn;

          inFlightUrls.add(url);

          // progress hooks
          if (!(url in loadingProgressMap)) loadingProgressMap[url] = 0;
          if (typeof loader.on === "function") {
            loader.on(LoaderEvent.LoadProgress, ({ progress, id }: { progress: number; id: string }) => {
              consolidateProgressThrottled({ id: id || url, progress });
            });
            loader.on(LoaderEvent.LoadCancelled, (id: string) => {
              delete loadingProgressMap[id || url];
              consolidateProgressInternal({ id: id || url, progress: 1 });
              inFlightUrls.delete(id || url);
              if (inflightModels === 0 && Object.keys(loadingProgressMap).length === 0) finishBatchIfPossible();
            });
          }

          try {
            console.log("[speckle] loadObject start", url);
            await v.loadObject(loader, true);
            console.log("[speckle] loadObject done", url);
            const rid = (loader as { resource?: string }).resource || url;
            consolidateProgressInternal({ id: rid, progress: 1 });
            delete loadingProgressMap[rid];
          } catch (e) {
            console.error("[speckle] loadObject error", url, e);
            if (stale()) return; // viewer disposed or load cancelled meanwhile — nothing to report
            throw e;
          } finally {
            inFlightUrls.delete(url);
          }

          // mark resource done
          const rid = (loader as { resource?: string }).resource || url;
          consolidateProgressInternal({ id: rid, progress: 1 });
          delete loadingProgressMap[rid];
        })
      );

      // snapshot AFTER & diff
      if (stale()) return;
      const after = getAllNodeIds(v);
      const added: string[] = [];
      for (const id of after) if (!before.has(id)) added.push(id);
      nodeIdsByModel.set(modelId, added);
      rvsByModel.set(modelId, collectRVsForNodeIds(v, added));

      // sanitize once after load
      sanitizeScene(v);

      loaded.value.add(modelId);
      visible.value.add(modelId);
      // The scene gained objects: the label indexes built for the old scene no longer cover it.
      useSpeckleFilters().invalidate();
      v.requestRender?.();
    })();

    loadingByModel.set(modelId, job);
    try {
      await job;
    } finally {
      loadingByModel.delete(modelId);
      cancelledModels.delete(modelId);
      finishBatchIfPossible();
    }
  }

  // The render views cached when the model loaded, falling back to the tree. The tree lookup alone is not
  // reliable: findId returns the first node with that id, which is not always the one that carries geometry.
  function renderViewsForModel(v: Viewer, modelId: string): NodeRenderView[] {
    const cached = rvsByModel.get(modelId);
    if (cached?.length) return cached;
    const renderTree = v.getWorldTree().getRenderTree(modelId);
    const node = v.getWorldTree().findId(modelId);
    if (!renderTree || !node || !node[0]) return [];
    return renderTree.getRenderViewsForNode(node[0]) as NodeRenderView[];
  }

  // The picker hides a model through the renderer, outside FilteringExtension, which resets every
  // material on each state apply — re-applied on FilteringStateSet so a hidden model doesn't reappear.
  function reapplyHiddenModels() {
    const v = viewer.value;
    if (!v) return;
    let touched = false;
    for (const modelId of loaded.value) {
      if (visible.value.has(modelId)) continue;
      const rvs = renderViewsForModel(v, modelId);
      if (!rvs.length) continue;
      v.getRenderer().setMaterial(rvs, { filterType: FilterMaterialType.HIDDEN });
      touched = true;
    }
    if (touched) v.requestRender(UpdateFlags.RENDER_RESET | UpdateFlags.SHADOWS);
  }

  async function hideModel(modelId: string) {
    try {
      selectRef.value?.clearSelection?.();
    } catch {
      // intentional
    }
    filters.clearFilters();

    const v = viewerForAction();
    if (!v) return;

    resetFiltersAndSelectionHard();

    // Failures throw rather than warn, and the bookkeeping moves only on success — the caller must be
    // able to know whether the model is actually hidden.
    const rvs = renderViewsForModel(v, modelId);
    if (!rvs.length) throw new Error(`[hideModel] no render views found for model ${modelId}`);

    try {
      await v.getRenderer().setMaterial(rvs, { filterType: FilterMaterialType.HIDDEN });
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }

    visible.value.delete(modelId);
    v.requestRender(UpdateFlags.RENDER_RESET | UpdateFlags.SHADOWS);
  }

  async function showModel(modelId: string) {
    try {
      selectRef.value?.clearSelection?.();
    } catch {
      // intentional
    }
    filters.clearFilters();

    const v = viewerForAction();
    if (!v) return;

    resetFiltersAndSelectionHard();

    // Same contract as hideModel: throw on failure, mark visible only once the materials are restored.
    const rvs = renderViewsForModel(v, modelId);
    if (!rvs.length) throw new Error(`[showModel] no render views found for model ${modelId}`);

    try {
      const renderer = v.getRenderer();
      for (const rv of rvs) {
        // restore default
        const batchMat = renderer.getBatchMaterial(rv);
        if (batchMat) {
          renderer.setMaterial([rv], batchMat);
        }
      }
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }

    visible.value.add(modelId);
    v.requestRender(UpdateFlags.RENDER_RESET | UpdateFlags.SHADOWS);
  }

  async function unloadModel(modelId: string) {
    const v = viewerForAction();
    if (!v) return;

    // if a load is in-flight for this model, wait for it
    const pending = loadingByModel.get(modelId);
    if (pending) await pending;

    resetFiltersAndSelectionHard();

    const urls = urlsByModel.get(modelId) ?? [];

    if (urls.length) {
      // try to unload every resource; don't fail the whole call if one rejects
      await Promise.allSettled(urls.map((u) => v.unloadObject(u)));
    }

    // cleanup bookkeeping
    urlsByModel.delete(modelId);
    nodeIdsByModel.delete(modelId);
    rvsByModel.delete(modelId);
    useSpeckleFilters().invalidate();
    loaded.value.delete(modelId);
    visible.value.delete(modelId);

    // anitize after removals
    sanitizeScene(v);
    v.requestRender?.();
  }

  function clearSelection() {
    try {
      selectRef.value?.clearSelection?.();
    } catch {
      // intentional
    }
  }

  /** Internal: reset filters+selection (safe if filters store not created yet) */
  function resetFiltersAndSelectionHard() {
    clearSelection();
    try {
      const filters = useSpeckleFilters();
      filters.clearFilters();
    } catch {
      // filters store might not be initialized yet — ignore
    }
  }

  function recenterView() {
    const v = viewer.value;
    const cam = cameraRef.value as unknown as {
      setCameraView: (arg: unknown, fit?: boolean) => void;
      setPerspectiveCameraOn?: () => void;
    };
    if (!v || !cam) return;

    try {
      const filters = useSpeckleFilters();
      const ids = filters.getZoomTargets?.() ?? [];
      // Only ids that still resolve to geometry: a stale or unloaded set yields an empty box and no move.
      if (ids.length && filters.hasRenderableIds(ids)) {
        cam.setCameraView(ids, true);
        return;
      }
    } catch {
      // intentional
    }

    const wt = v.getWorldTree() as unknown as { worldBox?: unknown };
    const worldBox = wt.worldBox ?? (v as any).World?.worldBox;

    if (worldBox) {
      cam.setCameraView(worldBox, true);
      // Bound zoom to the model's scale so the wheel can't fly past it into empty space. Derived from the
      // world box on each recenter so it tracks whatever is loaded; non-fatal — leaves zoom unbounded on failure.
      try {
        const box = worldBox as { min: { distanceTo: (other: unknown) => number }; max: unknown };
        const diagonal = box.min.distanceTo(box.max);
        if (diagonal > 0 && cameraRef.value) {
          cameraRef.value.options = {
            ...cameraRef.value.options,
            infiniteZoom: false,
            maximumRadius: diagonal * 4,
            minimumRadius: diagonal * 0.002,
          };
        }
      } catch {
        // intentional: leave zoom unbounded if the world box can't be measured
      }
    } else {
      cam.setPerspectiveCameraOn?.();
    }
  }

  async function cancelAllLoads(unload = true) {
    if (!viewer.value) return;
    const v = viewer.value;
    // Mark every model still walking its urls, so the loop stops before the next url rather than after.
    for (const modelId of loadingByModel.keys()) cancelledModels.add(modelId);
    const urls = [...inFlightUrls];
    inFlightUrls.clear();
    await Promise.allSettled(urls.map((u) => v.cancelLoad(u, unload).catch(() => {})));
    // progress cleanup (defensive)
    for (const u of urls) delete loadingProgressMap[u];
    finishBatchIfPossible();
  }

  async function cancelModelLoad(modelId: string, unload = true) {
    if (!viewer.value) return;
    const v = viewer.value;
    if (loadingByModel.has(modelId)) cancelledModels.add(modelId);
    const urls = urlsByModel.get(modelId) ?? [];
    urlsByModel.delete(modelId);
    await Promise.allSettled(urls.map((u) => v.cancelLoad(u, unload).catch(() => {})));
    for (const u of urls) {
      inFlightUrls.delete(u);
      delete loadingProgressMap[u];
    }
    finishBatchIfPossible();
  }

  /** Pulls a fresh Speckle token; returns whether one is available. The previous token is kept on failure (a transient 500 shouldn't drop a working session), so callers can't infer "no token" from `authToken` alone. */
  async function refreshToken(): Promise<boolean> {
    const token = await getSpeckleToken();
    if (token) {
      authToken.value = token;
    }
    return !!authToken.value;
  }

  return {
    // viewer state
    enableViewer,
    viewer,
    isReady,
    webGlUnavailable,
    loaded,
    visible,

    cameraRef,
    sectionRef,
    modesRef,
    selectRef,
    filteringRef,
    measureRef,
    vertexDragRef,
    measurementsList,

    hasModels,

    // loading UI
    loading,
    loadProgress,

    // lifecycle
    initViewer,
    disposeViewer,

    // actions
    loadModel,
    hideModel,
    showModel,
    unloadModel,

    // utilities
    recenterView,

    cancelAllLoads,
    cancelModelLoad,
    clearSelection,
    resetFiltersAndSelectionHard,

    getToken: () => {
      return authToken.value;
    },
    refreshToken,
  };
});
